'use client';

import Link from 'next/link';

import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import { MoreHorizontal } from 'lucide-react';

import { cn } from '@/lib/utils';
import { IMenuImage, IMenuItem } from '@/types/navigation';
import {
  HeaderElementDataType,
  LayoutClassesType,
} from '@/components/sections/builder/elements/header/header/type';
import { DropdownMenu } from '@/components/sections/builder/elements/header/header/content/dropdown-menu';
import { MegaMenu } from '@/components/sections/builder/elements/header/header/content/mega-menu';
import { useEffect, useRef, useState } from 'react';

type NavigationMenuProps = {
  menuItems: IMenuItem[];
  layoutClasses: LayoutClassesType;
  data: HeaderElementDataType;
  themeId?: string;
  tokenPreview?: string;
  preview?: boolean;
  isMegaMenu: boolean;
  isLoadingMenu?: boolean;
  setIsNavOpenAction: React.Dispatch<React.SetStateAction<boolean>>;
  createUrlAction: (
    url_path: string,
    opts?: {
      themeId?: string;
      token?: string;
      preview?: boolean;
    }
  ) => string;
};

export function NavigationMenuDesktop({
  menuItems,
  layoutClasses,
  data,
  themeId,
  tokenPreview,
  preview,
  isMegaMenu,
  isLoadingMenu,
  setIsNavOpenAction: setIsNavOpen,
  createUrlAction: createUrl,
}: NavigationMenuProps) {
  const [visibleItems, setVisibleItems] = useState<number>(menuItems.length);
  const [isCalculating, setIsCalculating] = useState(true);

  const navRef = useRef<HTMLDivElement>(null);
  const position = data.logoPosition;

  const visibleMenuItems = menuItems.slice(0, visibleItems);
  const hiddenMenuItems = menuItems.slice(visibleItems);

  const hiddenImages = imageHiddenMenu(hiddenMenuItems);

  useEffect(() => {
    const calculateVisibleItems = () => {
      if (!navRef.current) return;

      const container = navRef.current.parentElement;
      if (!container) return;

      const containerWidth = container.offsetWidth;
      const logoWidth = 120;
      const paddingAndMargins = 64;
      const moreButtonWidth = 48;
      const actionsWidth = 200; // Width for action buttons

      let availableWidth;
      if (position === 'middle-center') {
        // For middle-center, menu is on left side, need to account for centered logo
        availableWidth = containerWidth / 2 - logoWidth / 2 - paddingAndMargins - moreButtonWidth;
      } else if (position === 'middle-left') {
        availableWidth = containerWidth / 2 - logoWidth / 2 - paddingAndMargins - moreButtonWidth;
      } else {
        availableWidth =
          containerWidth - logoWidth - paddingAndMargins - moreButtonWidth - actionsWidth;
      }

      let totalWidth = 0;
      let count = 0;

      for (let i = 0; i < menuItems.length; i++) {
        const item = menuItems[i];
        const estimatedWidth = item.title.length * 8 + 32; // Extra padding for navigation menu items

        if (totalWidth + estimatedWidth < availableWidth) {
          totalWidth += estimatedWidth;
          count = i + 1;
        } else {
          break;
        }
      }

      const minItems =
        containerWidth > 768
          ? position === 'middle-center' || position === 'middle-left'
            ? 1
            : 2
          : 1;
      const maxItems =
        position === 'middle-center' || position === 'middle-left'
          ? Math.min(6, containerWidth < 640 ? 1 : 6)
          : containerWidth < 640
          ? 2
          : menuItems.length;

      setVisibleItems(Math.min(maxItems, Math.max(minItems, count)));
      setIsCalculating(false);
    };

    const timer = setTimeout(calculateVisibleItems, 100);
    calculateVisibleItems();

    const resizeHandler = () => {
      setIsCalculating(true);
      setTimeout(calculateVisibleItems, 50);
    };

    window.addEventListener('resize', resizeHandler);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', resizeHandler);
    };
  }, [position, menuItems]);

  return (
    <div
      className={cn(
        layoutClasses.nav,
        (data.logoPosition === 'top-center' || data.logoPosition === 'top-left') &&
          (data.desktopMenuPosition === 'center' || !data.desktopMenuPosition) &&
          'justify-center',
        (data.logoPosition === 'top-center' || data.logoPosition === 'top-left') &&
          data.desktopMenuPosition === 'left' &&
          'justify-start',
        data.desktopMenuPosition === 'right' && 'justify-end'
      )}
      ref={navRef}
    >
      {isLoadingMenu ? (
        <div
          className={cn(
            'flex w-full gap-3',
            data.logoPosition === 'middle-left' && 'justify-center',
            !data.logoPosition?.includes('middle') &&
              `justify-${
                data.desktopMenuPosition === 'center' || !data.desktopMenuPosition
                  ? 'center'
                  : data.desktopMenuPosition === 'left'
                  ? 'start'
                  : 'end'
              }`
          )}
        >
          {Array(4)
            .fill(0)
            .map((_, idx) => (
              <div
                key={idx}
                className='mb-2 h-5 w-20 animate-pulse rounded bg-gray-200 dark:bg-gray-700'
              />
            ))}
        </div>
      ) : (
        <NavigationMenu
          className='relative z-50 w-full'
          onValueChange={(value) => isMegaMenu && setIsNavOpen(!!value)}
          viewport={isMegaMenu}
          skipDelayDuration={200}
        >
          <NavigationMenuList className='gap-navigation group mx-auto flex w-full max-w-screen-xl justify-between'>
            {!isCalculating &&
              visibleMenuItems.map((menu, index) => (
                <NavigationMenuItem key={index}>
                  {menu.children.length > 0 ? (
                    <>
                      <NavigationMenuTrigger
                        className={cn(
                          'menu-item-with-hover-border relative hidden flex-shrink-0 cursor-pointer items-center gap-x-2 text-base font-normal after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:origin-bottom-left after:scale-x-0 after:transition-transform after:duration-300 after:content-[""] hover:after:scale-x-100 lg:flex',
                          menu.is_highlight && 'rounded bg-red-500 px-2 py-1 text-white'
                        )}
                      >
                        {menu.title}
                      </NavigationMenuTrigger>

                      <NavigationMenuContent
                        className={cn(
                          'transition-all duration-300 will-change-[opacity]',
                          'data-[state=closed]:opacity-0 data-[state=open]:opacity-100',
                          'data-[motion^=from-]:animate-in data-[motion^=to-]:animate-out',
                          'data-[motion^=from-]:fade-in data-[motion^=to-]:fade-out',
                          'data-[motion=from-end]:slide-in-from-right-52 data-[motion=from-start]:slide-in-from-left-52',
                          'data-[motion=to-end]:slide-out-to-right-52 data-[motion=to-start]:slide-out-to-left-52',
                          isMegaMenu && 'left-0 top-0 w-full md:absolute md:w-auto',
                          !isMegaMenu && 'rounded-none'
                        )}
                      >
                        <div
                          className={cn(
                            isMegaMenu && `fixed left-0 mt-2.5 w-screen`,
                            !isMegaMenu && 'w-[300px] rounded-none',
                            data?.colorSchemeMenuHeader?.background
                              ? 'bg-[--menu-background] text-[--menu-text]'
                              : 'bg-white'
                          )}
                          style={
                            {
                              '--menu-background': data?.colorSchemeMenuHeader?.background,
                              '--menu-text': data?.colorSchemeMenuHeader?.text,
                            } as React.CSSProperties
                          }
                        >
                          <div
                            className={cn(isMegaMenu ? 'border-b border-t border-gray-200' : '')}
                          >
                            {isMegaMenu ? (
                              // MEGA MENU
                              <MegaMenu
                                menu={menu.children}
                                menuImages={menu.images}
                                themeId={themeId}
                                token={tokenPreview}
                                preview={preview}
                                createUrl={createUrl}
                                data={data}
                              />
                            ) : (
                              // DROPDOWN MENU
                              <DropdownMenu
                                menu={menu.children}
                                themeId={themeId}
                                token={tokenPreview}
                                preview={preview}
                                createUrl={createUrl}
                              />
                            )}
                          </div>
                        </div>
                      </NavigationMenuContent>
                    </>
                  ) : (
                    <Link
                      href={createUrl(menu.url_path || '', {
                        themeId,
                        token: tokenPreview as string,
                        preview,
                      })}
                      className={cn(
                        'menu-item-with-hover-border relative hidden flex-shrink-0 cursor-pointer items-center gap-x-2 text-base after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:origin-bottom-left after:scale-x-0 after:transition-transform after:duration-300 after:content-[""] hover:after:scale-x-100 lg:flex',
                        menu.is_highlight && 'rounded bg-red-500 px-2 py-1 text-white'
                      )}
                    >
                      {menu.title}
                    </Link>
                  )}
                </NavigationMenuItem>
              ))}

            {!isCalculating && hiddenMenuItems.length > 0 && (
              <NavigationMenuItem className='hidden lg:flex'>
                <NavigationMenuTrigger
                  className={cn(
                    'menu-item-with-hover-border relative hidden flex-shrink-0 cursor-pointer items-center gap-x-2 text-base after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:origin-bottom-left after:scale-x-0 after:transition-transform after:duration-300 after:content-[""] hover:after:scale-x-100 lg:flex'
                  )}
                  hideIcon
                >
                  <MoreHorizontal className='h-4 w-4' />
                  <span className='sr-only'>More menu items</span>
                </NavigationMenuTrigger>
                <NavigationMenuContent
                  className={cn(
                    isMegaMenu &&
                      'left-0 top-0 w-full data-[motion^=from-]:animate-in data-[motion^=to-]:animate-out data-[motion^=from-]:fade-in data-[motion^=to-]:fade-out data-[motion=from-end]:slide-in-from-right-52 data-[motion=from-start]:slide-in-from-left-52 data-[motion=to-end]:slide-out-to-right-52 data-[motion=to-start]:slide-out-to-left-52 md:absolute md:w-auto',
                    !isMegaMenu && 'rounded-none'
                  )}
                >
                  <div
                    className={cn(
                      isMegaMenu && 'fixed left-0 mt-2.5 w-screen',
                      !isMegaMenu && 'w-[300px] rounded-none',
                      data?.colorSchemeMenuHeader?.background
                        ? 'bg-[--menu-background] text-[--menu-text]'
                        : 'bg-white'
                    )}
                    style={
                      {
                        '--menu-background': data?.colorSchemeMenuHeader?.background,
                        '--menu-text': data?.colorSchemeMenuHeader?.text,
                      } as React.CSSProperties
                    }
                  >
                    <div className={cn(isMegaMenu ? 'border-b border-t  border-gray-200' : '')}>
                      {isMegaMenu ? (
                        // MEGA MENU
                        <MegaMenu
                          menu={hiddenMenuItems}
                          menuImages={hiddenImages}
                          themeId={themeId}
                          token={tokenPreview}
                          preview={preview}
                          createUrl={createUrl}
                          data={data}
                        />
                      ) : (
                        // DROPDOWN MENU
                        <DropdownMenu
                          menu={hiddenMenuItems}
                          themeId={themeId}
                          token={tokenPreview}
                          preview={preview}
                          createUrl={createUrl}
                        />
                      )}
                    </div>
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>
            )}
          </NavigationMenuList>
        </NavigationMenu>
      )}
    </div>
  );
}

const imageHiddenMenu = (data: IMenuItem[]): IMenuImage[] => {
  const result: IMenuImage[] = [];

  for (const item of data) {
    for (const img of item.images || []) {
      if (result.length < 3) {
        result.push(img);
      }
    }

    if (result.length >= 3) break;
  }

  return result;
};
